"use client";

import { motion, Variants } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { ArrowRight } from "lucide-react";

const stats = [
  { value: "10x", label: "Retorno médio em ROAS" },
  { value: "200+", label: "Campanhas gerenciadas" },
  { value: "98%", label: "Clientes satisfeitos" },
];

export function Hero() {
  const container: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
  };
  const item: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16 pb-20 overflow-hidden">
      {/* Background growth chart — blurred and faded */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <svg
          viewBox="0 0 1200 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute bottom-0 right-0 w-[90%] h-auto opacity-[0.06] blur-[2px]"
          preserveAspectRatio="none"
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <line key={`h-${i}`} x1="0" y1={i * 100 + 50} x2="1200" y2={i * 100 + 50} stroke="white" strokeWidth="0.5" opacity="0.3" />
          ))}
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
            <line key={`v-${i}`} x1={i * 100 + 100} y1="50" x2={i * 100 + 100} y2="550" stroke="white" strokeWidth="0.5" opacity="0.3" />
          ))}
          {[
            { x: 100, h: 60 }, { x: 200, h: 90 }, { x: 300, h: 75 },
            { x: 400, h: 130 }, { x: 500, h: 160 }, { x: 600, h: 150 },
            { x: 700, h: 210 }, { x: 800, h: 270 }, { x: 900, h: 320 },
            { x: 1000, h: 390 }, { x: 1100, h: 460 },
          ].map((bar, i) => (
            <motion.rect
              key={`bar-${i}`}
              x={bar.x - 25}
              y={550 - bar.h}
              width="50"
              height={bar.h}
              rx="4"
              fill="url(#chartGrad)"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 1.2, delay: 0.6 + i * 0.08, ease: "easeOut" }}
              style={{ originY: 1, transformBox: "fill-box" }}
            />
          ))}
          <motion.path
            d="M 100 490 C 200 480, 250 470, 300 475 C 400 460, 450 430, 500 400 C 550 380, 600 390, 700 340 C 800 280, 850 250, 900 220 C 950 190, 1000 150, 1100 90"
            stroke="#C9956B"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, delay: 0.8, ease: "easeInOut" }}
          />
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C9956B" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#C9956B" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full px-4 sm:px-6 flex flex-col items-center text-center"
      >
        {/* Hero Logo — centered */}
        <motion.div variants={item} className="mb-8 sm:mb-10 flex flex-col items-center">
          {/* Decorative lines hidden on mobile to avoid misalignment */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mb-3">
            <div className="hidden sm:block h-px w-16 bg-gradient-to-r from-transparent to-[#C9956B]/40" />
            <Logo size="lg" showSubtitle />
            <div className="hidden sm:block h-px w-16 bg-gradient-to-l from-transparent to-[#C9956B]/40" />
          </div>
          <p className="text-[10px] sm:text-[11px] font-sans font-medium uppercase text-[#9e8b7a] text-center tracking-[0.25em] sm:tracking-[0.35em]">
            Posicionamento • Presença Digital • Resultados
          </p>
        </motion.div>

        {/* Heading */}
        <motion.h1
          variants={item}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.75rem] font-bold leading-[1.1] tracking-tight mb-6 max-w-3xl px-2"
        >
          Elevamos sua marca com{" "}
          <span className="text-[#D4A574]">presença digital</span>{" "}
          que gera resultados.
        </motion.h1>

        {/* Subheading */}
        <motion.p
          variants={item}
          className="text-base sm:text-lg text-zinc-400 mb-10 max-w-xl leading-relaxed px-2"
        >
          Posicionamento estratégico, presença digital consistente e resultados mensuráveis. A Aura Digital transforma sua marca em referência no mercado.
        </motion.p>

        {/* CTAs */}
        <motion.div variants={item} className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14 sm:mb-16 w-full sm:w-auto px-2">
          <Button className="w-full sm:w-auto bg-[#C9956B] hover:bg-[#D4A574] text-[#0a0a0a] rounded-lg h-11 px-6 text-[14px] font-medium transition-colors">
            Agendar Consultoria Grátis
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto rounded-lg h-11 px-6 text-[14px] font-medium border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            Ver Resultados
          </Button>
        </motion.div>

        {/* Stats row — always 3 columns */}
        <motion.div variants={item} className="grid grid-cols-3 gap-4 sm:gap-8 md:gap-12 w-full max-w-md sm:max-w-lg">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-white">{s.value}</p>
              <p className="text-[11px] sm:text-[13px] text-zinc-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
